import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useCallback } from "react";
import { graphScopes } from "./authConfig";
import { getFreshIdToken } from "./freshIdToken";

/**
 * Returns the signed-in user's Entra ID token (audience = our own app
 * registration), not a Graph access token. Any silent token request returns
 * both, so we reuse the Graph scopes and take idToken instead of
 * accessToken — this is what api/initiative-assist verifies, since it lets
 * us confirm the caller is a signed-in tenant user without exposing a
 * custom API scope.
 */
export function useAppIdToken() {
  const { instance, accounts } = useMsal();

  return useCallback(async (): Promise<string> => {
    const account = instance.getActiveAccount() ?? accounts[0];
    if (!account) {
      throw new Error("No signed-in account.");
    }
    try {
      return await getFreshIdToken(instance, account);
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        const result = await instance.acquireTokenPopup({ scopes: graphScopes });
        return result.idToken;
      }
      throw err;
    }
  }, [accounts, instance]);
}
